import { Link } from "react-router-dom";
import { ArrowRight, Brain, Pill, ClipboardList, HeartHandshake, ShieldCheck, Sparkles, Users, Stethoscope, Video, MapPin, CalendarDays, Quote, Check } from "lucide-react";
import SiteLayout from "../components/SiteLayout";
import SEO from "../components/SEO";
import CTABanner from "../components/CTABanner";
import CareMatchQuiz from "../components/CareMatchQuiz";
import MarylandCoverage, { MARYLAND_TAGLINE } from "../components/MarylandCoverage";

const services = [
 { icon: Brain, title: "Psychiatric Evaluation", desc: "Comprehensive diagnostic assessments that uncover the root of what you're experiencing, without rushing." },
 { icon: Pill, title: "Medication Management", desc: "Evidence-based prescribing with thoughtful adjustments and clear conversations about every option." },
 { icon: ClipboardList, title: "Treatment Planning", desc: "Plans that fit your life, your goals, and your culture, never one-size-fits-all." },
 { icon: Users, title: "Collaborative Care", desc: "We coordinate with primary care, therapists, and family, with consent, so your plan stays unified." },
 { icon: Video, title: "Telehealth Visits", desc: "Secure, HIPAA-aligned video visits delivered to wherever you feel safe across Maryland." },
 { icon: Stethoscope, title: "Initial Consultation", desc: "Not sure where to start? A short consultation helps map the right next step for your care." },
];

const benefits = [
 { title: "Seen within a week", desc: "Skip the long waitlists. Most patients are seen 5-7 days after booking." },
 { title: "Care that listens", desc: "Unhurried sessions, clear plans, and clinicians who treat you like a person, not a chart." },
 { title: "Anywhere, secure", desc: "HIPAA-aligned video visits from your phone, laptop, or tablet, wherever you feel safe." },
 { title: "Whole-life approach", desc: "We coordinate with therapists, primary care, and family when invited. You stay at the centre." },
];

const steps = [
 { n: "01", title: "Book in minutes", desc: "Pick a time that works. No phone tag, no maze of forms." },
 { n: "02", title: "Meet your psychiatrist", desc: "A 50-minute virtual evaluation focused on you and what you're carrying." },
 { n: "03", title: "Receive a plan", desc: "Clear, collaborative, and adjusted as you grow." },
 { n: "04", title: "Stay supported", desc: "Follow-up visits, secure messaging, and steady care between appointments." },
];

const testimonials = [
 { quote: "For the first time in years, I felt heard. They explained every option and let me decide. That made the difference.", who: "Patient, age 34" },
 { quote: "Telehealth meant I could finally start care while balancing two kids and a full-time job. The team made it feel simple.", who: "Patient, age 41" },
 { quote: "What stood out was the warmth, and the follow-up. They actually checked in, even when I didn't reach out first.", who: "Patient, age 27" },
];

export default function Home() {
 const businessLd = {
 "@context": "https://schema.org",
 "@type": ["MedicalBusiness", "LocalBusiness"],
 name: "Golden Life Standard Health",
 alternateName: "Golden Life Standard Health Telepsychiatry",
 url: "https://goldenlifesh.com/",
 logo: "https://goldenlifesh.com/brand/logo.png",
 image: "https://goldenlifesh.com/home-telehealth.png",
 description: "Compassionate online psychiatric care and telepsychiatry for Maryland residents. Same-week evaluations, medication management, and secure video follow-ups from licensed clinicians.",
 telephone: "+1-443-891-4248",
 email: "care@goldenlifesh.com",
 priceRange: "$$",
 medicalSpecialty: "Psychiatric",
 address: {
 "@type": "PostalAddress",
 addressRegion: "MD",
 addressCountry: "US",
 },
 areaServed: [
 { "@type": "State", name: "Maryland" },
 { "@type": "AdministrativeArea", name: "Maryland, United States" },
 ],
 openingHoursSpecification: [
 { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], opens: "08:00", closes: "20:00" },
 ],
 availableService: [
 { "@type": "MedicalTherapy", name: "New patient psychiatric evaluation" },
 { "@type": "MedicalTherapy", name: "Medication management" },
 { "@type": "MedicalTherapy", name: "Follow-up psychiatric care" },
 { "@type": "MedicalTherapy", name: "Telehealth psychiatric consultation" },
 ],
 sameAs: [],
 };
 return (
 <SiteLayout>
 <SEO
 title="Online Psychiatry in Maryland"
 description="Compassionate online psychiatric care for Maryland adults. Same-week evaluations, medication management, and follow-ups by telehealth from licensed clinicians."
 path="/"
 />
 <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(businessLd) }} />
 {/* HERO */}
 <section data-testid="hero-section" className="relative overflow-hidden">
 <div aria-hidden className="absolute inset-0 -z-10">
 <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream to-sage-50/40" />
 <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-gold-200/30 blur-3xl" />
 <div className="absolute -bottom-32 -left-20 w-[30rem] h-[30rem] rounded-full bg-sage-200/40 blur-3xl" />
 </div>

 <div className="container-pad max-w-7xl mx-auto pt-12 pb-20 lg:pt-20 lg:pb-28">
 <div className="grid lg:grid-cols-12 gap-12 items-center">
 <div className="lg:col-span-7 animate-fade-up">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Elevating mental wellness</p>
 <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-ink text-balance">
 Compassionate psychiatric care, <span className="italic text-sage-600">held to a golden standard.</span>
 </h1>
 <p className="mt-6 text-lg text-muted_text max-w-xl leading-relaxed">
 Online psychiatric and telepsychiatry services for adolescents, adults, and older adults, built on warmth, evidence, and respect for the life you're living.
 </p>
 <div className="mt-9 flex flex-wrap items-center gap-4">
 <Link to="/contact" data-testid="hero-book-btn" className="btn-gold">
 Book an appointment <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
 </Link>
 <Link to="/how-it-works" data-testid="hero-how-it-works-btn" className="btn-ghost">
 How care works
 </Link>
 </div>

 <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted_text">
 <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-sage-500" strokeWidth={2} /> Seen within 7 days</span>
 <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-sage-500" strokeWidth={2} /> HIPAA-aligned</span>
 <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-sage-500" strokeWidth={2} /> Most insurances welcomed</span>
 </div>

 <div className="mt-7 max-w-xl" data-testid="hero-md-map">
 <div className="flex justify-center">
 <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-paper border border-[#E8EBE4] text-xs text-ink/80 shadow-sm" data-testid="hero-coverage">
 <MapPin className="w-3.5 h-3.5 text-forest-700" strokeWidth={1.75} />
 {MARYLAND_TAGLINE}
 </div>
 </div>
 <div className="mt-3 relative rounded-3xl overflow-hidden border border-[#E8EBE4] bg-cream shadow-[0_24px_50px_-25px_rgba(26,43,51,0.25)]">
 <img
 src="/maryland-map.png"
 alt="Map of Maryland, the region we serve via telehealth"
 className="w-full h-auto block"
 loading="lazy"
 />
 </div>
 <div className="flex justify-center mt-3">
 <div className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-paper border border-[#E8EBE4] text-xs font-medium text-ink/80 shadow-sm">
 <MapPin className="w-3.5 h-3.5 text-forest-700" strokeWidth={2} />
 Serving all of Maryland, virtually
 </div>
 </div>
 </div>
 </div>

 <div className="lg:col-span-5 animate-fade-in">
 <div className="relative">
 <div className="absolute -top-6 -left-6 w-32 h-32 rounded-3xl bg-gold-500/15 -z-10" />
 <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-3xl bg-sage-500/15 -z-10" />
 <img
 src="https://images.unsplash.com/photo-1758691462749-a95ce1bd7f96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHx0ZWxlaGVhbHRoJTIwdGhlcmFwaXN0JTIwdmlkZW8lMjBjYWxsfGVufDB8fHx8MTc3ODAwMjM4MXww&ixlib=rb-4.1.0&q=85"
 alt="A clinician and patient on a professional telehealth video call"
 className="rounded-3xl w-full h-[460px] lg:h-[560px] object-cover shadow-[0_40px_80px_-30px_rgba(26,43,51,0.35)]"
 />
 <div className="absolute -bottom-8 left-6 right-6 sm:right-auto sm:max-w-xs bg-paper rounded-2xl p-5 border border-[#E8EBE4] shadow-[0_24px_50px_-20px_rgba(26,43,51,0.2)]">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-full bg-gold-500/15 flex items-center justify-center">
 <Video className="w-5 h-5 text-gold-600" strokeWidth={1.75} />
 </div>
 <div>
 <p className="font-heading text-sm font-semibold text-ink">Secure telehealth visits</p>
 <p className="text-xs text-muted_text">Evening & weekend slots available</p>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* SERVICES OVERVIEW */}
 <section data-testid="services-overview" className="container-pad max-w-7xl mx-auto section">
 <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Services</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink text-balance">
 Specialised psychiatric care, designed around you.
 </h2>
 </div>
 <div className="lg:col-span-5 lg:pl-8">
 <p className="text-muted_text leading-relaxed">
 From first evaluation to long-term follow-up, every service is delivered with clinical rigour and unhurried attention. We tailor care across mood, anxiety, trauma, ADHD, sleep, and substance use challenges.
 </p>
 </div>
 </div>

 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
 {services.map((s, i) => (
 <div key={s.title} data-testid={`service-card-${i}`}
 className="group card-soft p-7 hover:-translate-y-1 transition-transform duration-300 ease-out">
 <div className="w-12 h-12 rounded-2xl bg-sage-50 flex items-center justify-center text-sage-700 group-hover:bg-gold-500/15 group-hover:text-gold-600 transition-colors">
 <s.icon className="w-6 h-6" strokeWidth={1.5} />
 </div>
 <h3 className="mt-6 font-heading text-xl font-semibold text-ink">{s.title}</h3>
 <p className="mt-3 text-sm text-muted_text leading-relaxed">{s.desc}</p>
 </div>
 ))}
 </div>

 <div className="mt-10">
 <Link to="/services" data-testid="all-services-link" className="inline-flex items-center gap-2 text-sm font-semibold text-sage-700 hover:text-gold-600 transition-colors">
 See all services <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
 </Link>
 </div>
 </section>

 {/* CARE MATCH QUIZ */}
 <CareMatchQuiz />

 {/* HOW IT WORKS */}
 <section data-testid="how-it-works-overview" className="bg-paper">
 <div className="container-pad max-w-7xl mx-auto section">
 <div className="grid lg:grid-cols-12 gap-12">
 <div className="lg:col-span-5">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> How it works</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink text-balance">
 A path to care, made simple.
 </h2>
 <p className="mt-5 text-muted_text leading-relaxed max-w-md">
 Four steady steps from your first booking to ongoing support, no waitlists, no jargon, no guesswork.
 </p>
 <Link to="/how-it-works" data-testid="how-it-works-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-sage-700 hover:text-gold-600">
 Walk through the journey <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
 </Link>
 </div>
 <div className="lg:col-span-7">
 <ol className="grid sm:grid-cols-2 gap-5">
 {steps.map((s) => (
 <li key={s.n} data-testid={`step-${s.n}`} className="rounded-3xl bg-cream p-7 border border-[#E8EBE4]">
 <span className="font-heading text-gold-600 text-sm tracking-[0.2em]">{s.n}</span>
 <h3 className="mt-3 font-heading text-xl font-semibold text-ink">{s.title}</h3>
 <p className="mt-2 text-sm text-muted_text leading-relaxed">{s.desc}</p>
 </li>
 ))}
 </ol>
 </div>
 </div>
 </div>
 </section>

 {/* BENEFITS */}
 <section data-testid="benefits-section" className="container-pad max-w-7xl mx-auto section">
 <div className="grid lg:grid-cols-12 gap-12 items-center">
 <div className="lg:col-span-5 order-2 lg:order-1">
 <div className="relative">
 <img
 src="/home-telehealth.png"
 alt="A couple in a warm session on a telehealth video call with their female clinician visible on screen"
 className="rounded-3xl w-full h-[480px] object-cover shadow-[0_30px_60px_-30px_rgba(12,46,20,0.3)]"
 />
 <div className="absolute -top-6 -right-6 w-32 h-32 rounded-3xl bg-gold-500/15 -z-10" />
 </div>
 </div>
 <div className="lg:col-span-7 order-1 lg:order-2">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Why patients choose us</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink text-balance">
 The relief of being met where you are.
 </h2>
 <div className="mt-10 grid sm:grid-cols-2 gap-6">
 {benefits.map((b, i) => (
 <div key={b.title} data-testid={`benefit-${i}`} className="border-l-2 border-gold-500/60 pl-5">
 <h3 className="font-heading text-lg font-semibold text-ink">{b.title}</h3>
 <p className="mt-2 text-sm text-muted_text leading-relaxed">{b.desc}</p>
 </div>
 ))}
 </div>
 </div>
 </div>
 </section>

 {/* INSURANCE / ACCESSIBILITY */}
 <section data-testid="insurance-section" className="bg-sage-50/60 border-y border-[#E8EBE4]">
 <div className="container-pad max-w-7xl mx-auto py-16 lg:py-20">
 <div className="grid lg:grid-cols-12 gap-10 items-center">
 <div className="lg:col-span-7">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Accessibility</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink">
 Care that's reachable, financially and personally.
 </h2>
 <p className="mt-4 text-muted_text leading-relaxed max-w-2xl">
 We accept most major insurance plans and offer transparent self-pay options for those without coverage. If cost is a barrier, talk to us, sliding scale and referral pathways may apply.
 </p>
 <ul className="mt-7 grid sm:grid-cols-2 gap-3 text-sm text-ink">
 {["Aetna", "Blue Cross Blue Shield", "Cigna", "United Healthcare", "Medicare", "Self-pay options"].map((p) => (
 <li key={p} className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-sage-600" strokeWidth={2} /> {p}</li>
 ))}
 </ul>
 <div className="mt-8 card-soft p-6" data-testid="home-md-coverage">
 <MarylandCoverage />
 </div>
 </div>
 <div className="lg:col-span-5">
 <div className="card-soft p-7">
 <div className="flex items-center gap-4">
 <div className="w-12 h-12 rounded-2xl bg-gold-500/15 flex items-center justify-center text-gold-600">
 <Stethoscope className="w-6 h-6" strokeWidth={1.5} />
 </div>
 <div>
 <p className="font-heading font-semibold text-ink">Not sure if you're covered?</p>
 <p className="text-sm text-muted_text">Send us your plan info, we'll verify within one business day.</p>
 </div>
 </div>
 <Link to="/insurance" data-testid="insurance-cta" className="btn-primary mt-6 w-full">
 Verify my coverage <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
 </Link>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* SOCIAL PROOF, Trust strip */}
 <section data-testid="social-proof-section" className="bg-paper border-y border-[#E8EBE4]">
 <div className="container-pad max-w-7xl mx-auto py-14 lg:py-16">
 <div className="grid lg:grid-cols-12 gap-10 items-center">
 <div className="lg:col-span-5">
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Trusted across Maryland</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl tracking-tight text-ink text-balance">
 A growing community of patients, met with care.
 </h2>
 <p className="mt-4 text-muted_text leading-relaxed max-w-md">
 We're proud of the trust patients, families, and primary care partners place in us, and we work each visit to keep earning it.
 </p>
 </div>
 <div className="lg:col-span-7">
 <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
 {[
 { stat: "1,200+", label: "Visits delivered" },
 { stat: "4.9/5", label: "Average patient rating" },
 { stat: "≤ 7 days", label: "Typical first appointment" },
 { stat: "98%", label: "Would recommend us" },
 ].map((s, i) => (
 <div key={s.label} data-testid={`trust-stat-${i}`} className="rounded-2xl bg-cream border border-[#E8EBE4] p-5 text-center">
 <dt className="font-heading text-2xl lg:text-3xl text-forest-700 tracking-tight whitespace-nowrap">{s.stat}</dt>
 <dd className="mt-2 text-xs uppercase tracking-[0.14em] text-muted_text">{s.label}</dd>
 </div>
 ))}
 </dl>
 <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-ink/80">
 <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-forest-700" strokeWidth={1.75} /> HIPAA-aligned</span>
 <span className="inline-flex items-center gap-2"><HeartHandshake className="w-4 h-4 text-gold-600" strokeWidth={1.75} /> Trauma-informed care</span>
 <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-sage-700" strokeWidth={1.75} /> Culturally responsive</span>
 <span className="inline-flex items-center gap-2"><CalendarDays className="w-4 h-4 text-sage-600" strokeWidth={1.75} /> Evenings & weekends</span>
 </div>
 <p className="mt-4 text-[11px] text-muted_text/80">Placeholder metrics, replace with verified figures once available.</p>
 </div>
 </div>
 </div>
 </section>

 {/* TESTIMONIALS */}
 <section data-testid="testimonials-section" className="container-pad max-w-7xl mx-auto section">
 <div className="text-center max-w-2xl mx-auto">
 <p className="eyebrow justify-center"><span className="w-6 h-px bg-gold-500" /> What patients say</p>
 <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink text-balance">
 Real words from people we serve.
 </h2>
 </div>
 <div className="mt-14 grid md:grid-cols-3 gap-6">
 {testimonials.map((t, i) => (
 <figure key={i} data-testid={`testimonial-${i}`} className="card-soft p-8">
 <Quote className="w-7 h-7 text-gold-500" strokeWidth={1.5} />
 <blockquote className="mt-5 text-ink leading-relaxed">"{t.quote}"</blockquote>
 <figcaption className="mt-6 text-sm text-muted_text">, {t.who}</figcaption>
 </figure>
 ))}
 </div>
 <p className="mt-6 text-xs text-muted_text/80 text-center">Identifying details removed to protect patient privacy.</p>
 </section>

 <CTABanner />
 </SiteLayout>
 );
}
